/**
 * Stick-to-bottom scrolling for streaming surfaces (DEV-595).
 */
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Slack, in pixels, when deciding whether the container counts as "at the
 * bottom". Sub-pixel layout and fractional line heights mean the numbers
 * rarely land exactly on zero.
 */
const BOTTOM_TOLERANCE_PX = 24

/**
 * Slack, in pixels, when matching a scroll event against the offset we set
 * ourselves. Browsers can land a fraction of a pixel off the requested value.
 */
const SELF_SCROLL_TOLERANCE_PX = 2

export interface StickToBottom {
  /** Attach to the scrolling element — the one carrying `overflow-y-auto`. */
  containerRef: (node: HTMLElement | null) => void
  /** Attach to the element inside the container that grows as content arrives. */
  contentRef: (node: HTMLElement | null) => void
  /** True while the container is pinned to the bottom and auto-scrolling. */
  isPinned: boolean
  /** True once content has arrived below the fold while the user is scrolled away. */
  showJumpToLatest: boolean
  /** Re-pin to the bottom and resume auto-scroll. */
  jumpToLatest: () => void
}

/**
 * Auto-scrolls a streaming container only while the user is pinned to the
 * bottom. A user scroll away from the bottom unpins and stops auto-scroll;
 * scrolling back, or calling `jumpToLatest`, re-pins.
 *
 * Two details make that hold up under a live stream:
 *
 * - Scrolls we perform ourselves are recorded in `selfScrollTopRef` and skipped
 *   by the scroll handler, so auto-scroll cannot re-arm itself off its own
 *   scroll events. Every other scroll event is the user's and re-evaluates the
 *   pin. A stale recorded offset is harmless: it can only match a scroll that
 *   landed at the bottom, which is pinned either way.
 * - Content growth is observed with a `ResizeObserver` on the content element,
 *   so the pin is applied after layout rather than before. Late growth — an
 *   image loading, a code block finishing — is handled exactly like an arriving
 *   token, and never uses `scrollIntoView`.
 */
export function useStickToBottom(): StickToBottom {
  const [container, setContainer] = useState<HTMLElement | null>(null)
  const [content, setContent] = useState<HTMLElement | null>(null)

  const [isPinned, setIsPinned] = useState(true)
  const [showJumpToLatest, setShowJumpToLatest] = useState(false)

  // Mirrors `isPinned` so the scroll and resize handlers can read it without
  // being torn down and rebuilt on every pin change.
  const pinnedRef = useRef(true)
  // Offset we last set ourselves, used to tell our own scroll events from the
  // user's. Null once consumed.
  const selfScrollTopRef = useRef<number | null>(null)
  // Last observed content height, so a resize that shrinks or reflows content
  // does not read as new content arriving.
  const scrollHeightRef = useRef(0)

  const setPinned = useCallback((next: boolean) => {
    if (pinnedRef.current === next) return
    pinnedRef.current = next
    setIsPinned(next)
    if (next) setShowJumpToLatest(false)
  }, [])

  const scrollToBottom = useCallback(() => {
    if (!container) return
    const target = container.scrollHeight - container.clientHeight
    selfScrollTopRef.current = target
    if (container.scrollTop !== target) container.scrollTop = target
  }, [container])

  const jumpToLatest = useCallback(() => {
    setPinned(true)
    setShowJumpToLatest(false)
    scrollToBottom()
  }, [setPinned, scrollToBottom])

  // Re-evaluate the pin on user scrolls, ignoring the echo of our own.
  useEffect(() => {
    if (!container) return

    const handleScroll = () => {
      const selfScrollTop = selfScrollTopRef.current
      selfScrollTopRef.current = null
      if (
        selfScrollTop !== null &&
        Math.abs(container.scrollTop - selfScrollTop) <= SELF_SCROLL_TOLERANCE_PX
      ) {
        return
      }
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight
      setPinned(distanceFromBottom <= BOTTOM_TOLERANCE_PX)
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [container, setPinned])

  // Follow content growth after layout.
  useEffect(() => {
    if (!container || !content) return

    scrollHeightRef.current = container.scrollHeight

    const observer = new ResizeObserver(() => {
      const grew = container.scrollHeight > scrollHeightRef.current
      scrollHeightRef.current = container.scrollHeight
      if (pinnedRef.current) {
        scrollToBottom()
      } else if (grew) {
        setShowJumpToLatest(true)
      }
    })
    observer.observe(content)
    // The container too: resizing the viewport moves the bottom without
    // changing the content's height.
    observer.observe(container)
    return () => observer.disconnect()
  }, [container, content, scrollToBottom])

  return {
    containerRef: setContainer,
    contentRef: setContent,
    isPinned,
    showJumpToLatest,
    jumpToLatest,
  }
}
